import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { supabase } from "@/lib/supabase";
import { captureError } from '@/lib/sentry';

interface CustomField {
  id: string;
  label: string;
  data_type: string;
}

const DATA_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'boolean', label: 'Yes/No' },
  { value: 'select', label: 'Select' }
];

export function CustomFieldsSettings() {
  const [fields, setFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(true);
  const [newField, setNewField] = useState({ label: '', data_type: 'text' });
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();

  useEffect(() => {
    if (currentWorkspace) {
      fetchFields();
    }
  }, [currentWorkspace?.id]);

  const fetchFields = async () => {
    try {
      const { data, error } = await supabase
        .from('custom_fields')
        .select('*')
        .eq('workspace_id', currentWorkspace?.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setFields(data || []);
    } catch (error) {
      console.error('Error fetching custom fields:', error);
      captureError(error as Error, { feature: 'settings-custom-fields-settings-fetch-fields' });
      toast({
        title: "Error",
        description: "Failed to load custom fields",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const addField = async () => {
    if (!currentWorkspace || !newField.label.trim()) return;

    try {
      const { error } = await supabase
        .from('custom_fields')
        .insert({
          workspace_id: currentWorkspace.id,
          label: newField.label.trim(),
          data_type: newField.data_type,
        });

      if (error) throw error;

      toast({
        description: "Custom field added successfully",
      });

      setNewField({ label: '', data_type: 'text' });
      fetchFields();
    } catch (error) {
      console.error('Error adding custom field:', error);
      captureError(error as Error, { feature: 'settings-custom-fields-settings-add-field' });
      toast({
        title: "Error",
        description: "Failed to add custom field",
        variant: "destructive",
      });
    }
  };

  const deleteField = async (fieldId: string) => {
    try {
      const { error } = await supabase
        .from('custom_fields')
        .delete()
        .eq('id', fieldId);

      if (error) throw error;

      toast({
        description: "Custom field deleted successfully",
      });

      fetchFields();
    } catch (error) {
      console.error('Error deleting custom field:', error);
      captureError(error as Error, { feature: 'settings-custom-fields-settings-delete-field' });
      toast({
        title: "Error",
        description: "Failed to delete custom field",
        variant: "destructive",
      });
    }
  };

  if (!currentWorkspace) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No workspace selected
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">Custom Fields</h2>
        <p className="text-muted-foreground">
          Add custom fields to store additional information about your contacts
        </p>
      </div>

      <div className="space-y-4">
        {/* Add New Field */}
        <div className="flex items-center gap-4">
          <Input
            placeholder="Field label"
            value={newField.label}
            onChange={(e) => setNewField(prev => ({ ...prev, label: e.target.value }))}
            className="max-w-xs"
          />
          <Select
            value={newField.data_type}
            onValueChange={(value) => setNewField(prev => ({ ...prev, data_type: value }))}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select type" />
            </SelectTrigger>
            <SelectContent>
              {DATA_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={addField} disabled={!newField.label.trim()}>
            <Plus className="h-4 w-4 mr-2" />
            Add Field
          </Button>
        </div>

        {/* Field List */}
        {loading ? (
          <div className="text-center py-4">Loading custom fields...</div>
        ) : fields.length > 0 ? (
          <div className="space-y-2">
            {fields.map((field) => (
              <div
                key={field.id}
                className="flex items-center justify-between p-4 rounded-lg border bg-card"
              >
                <div>
                  <p className="font-medium">{field.label}</p>
                  <p className="text-sm text-muted-foreground capitalize">
                    {DATA_TYPES.find(t => t.value === field.data_type)?.label || field.data_type}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => deleteField(field.id)}
                  className="text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No custom fields yet. Add your first custom field above.
          </div>
        )}
      </div>
    </div>
  );
}